import {
  addDays,
  addMinutes,
  differenceInDays,
  format as dateFormat,
  isSameYear,
  getYear,
  isSameMonth,
  startOfDay,
} from 'date-fns';
import { RangeModifier } from 'react-day-picker/types/Modifiers';
import { utcToZonedTime } from 'date-fns-tz';
import { addMinutesToUtcDate } from 'src/utilities/addMinutesToUtcDate';
import { capitalize } from 'src/utilities/string-utils';
import {
  ILineOfCreditPayload,
  ILineOfCreditResponseType,
} from 'src/types/LineOfCredit';
import {
  currencyFormatter,
  transformDollarToPennies,
  transformPenniesToDollar,
} from 'src/utilities/currency-utils';

export interface ILocFormFields {
  creditName: string;
  amount: string;
  budgetLength: string;
  date: Date | RangeModifier;
  frequency: string;
  days: (string | boolean | number)[];
  isAdminIncludedInBudget?: boolean;
}

export const DAYS = [
  null,
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
];

const DATE_FORMAT = 'MMM d';
const TIME_FORMAT = 'h:mm a';

export const DaysOfWeekMap = {
  1: 'Monday',
  2: 'Tuesday',
  3: 'Wednesday',
  4: 'Thursday',
  5: 'Friday',
  6: 'Saturday',
  7: 'Sunday',
};

export const VALID_EVERY_DAY = [1, 2, 3, 4, 5, 6, 7];

export const UNLIMITED_BUDGET_PENNIES = 1000000;
export const ALL_DAY_MEAL_CREDIT_DURATION = 1439;
export const DEFAULT_WEEK_LENGTH_DAYS = 7;

export function getTimeRange(mealCredit: ILineOfCreditResponseType): string {
  const { first_instance_utc, duration_minutes } = mealCredit;

  if (duration_minutes === ALL_DAY_MEAL_CREDIT_DURATION) {
    return 'All day';
  }

  const utcTimeStart = getFirstInstanceDateUTC(first_instance_utc);
  const utcTimeEnd = addMinutesToUtcDate(first_instance_utc, duration_minutes);

  const formattedUtcStart = dateFormat(utcTimeStart, TIME_FORMAT);
  const formattedUtcEnd = dateFormat(utcTimeEnd, TIME_FORMAT);

  return `${formattedUtcStart} - ${formattedUtcEnd}`;
}

export function getWeekLocDates({
  loc,
  format,
  useDashForDateRange,
  useFullDates,
}: {
  loc: ILineOfCreditResponseType;
  format: string;
  lengthOfCredit?: number;
  useDashForDateRange?: boolean;
  useFullDates?: boolean;
}): string {
  const lengthOfCreditDays =
    loc.budget_reset_period || DEFAULT_WEEK_LENGTH_DAYS;

  const startDate = getFirstInstanceDateUTC(loc.first_instance_utc);
  const endDate = getWeeklyMealCreditEndDate(startDate, lengthOfCreditDays);
  const daysDiff = differenceInDays(new Date(), startDate);

  let formatToUse = format;
  const isSameYear = getYear(startDate) === getYear(endDate);
  const currentFormatIncludesYear = format.toLowerCase().indexOf('y') !== -1;

  // weekly credits can last up to 52 weeks so start and end date might cross years
  // we should include the year in this case so it doesn't look like the dates go backwards
  if (!isSameYear && !currentFormatIncludesYear) {
    formatToUse = `${format} yyyy`;
  }

  const isRepeatingCredit = loc.repeating;

  // if the credit repeats and more days than the length of the credit have passed, we need to update to the most recent date range
  if (isRepeatingCredit && daysDiff >= lengthOfCreditDays) {
    const daysPassesFromStartDate =
      Math.floor(daysDiff / lengthOfCreditDays) * lengthOfCreditDays;

    const newStartDate = addDays(startDate, daysPassesFromStartDate);
    const newEndDate = addDays(newStartDate, lengthOfCreditDays - 1);

    return formatDateRange({
      startDate: newStartDate,
      endDate: newEndDate,
      format: formatToUse,
      useDashForDateRange,
      useFullDates,
    });
  }
  return formatDateRange({
    startDate,
    endDate,
    format: formatToUse,
    useDashForDateRange,
    useFullDates,
  });
}

function formatDateRange({
  startDate,
  endDate,
  format,
  useDashForDateRange,
  useFullDates,
}: {
  startDate: Date;
  endDate: Date;
  format: string;
  useDashForDateRange?: boolean;
  useFullDates?: boolean;
}): string {
  const startDateFormatted = dateFormat(startDate, format);
  const areDatesSameMonth = isSameMonth(startDate, endDate);
  const areDatesSameYear = isSameYear(startDate, endDate);

  const endDateFormatted = `${dateFormat(
    endDate,
    areDatesSameMonth && areDatesSameYear && !useFullDates ? 'd' : format
  )}`;

  const dateRangeSeparator = !areDatesSameMonth ? ' to ' : ' - ';

  return !useDashForDateRange
    ? `from ${startDateFormatted}${dateRangeSeparator}${endDateFormatted}`
    : `${startDateFormatted} - ${endDateFormatted}`;
}

export function getDayLocFrequency(
  loc: ILineOfCreditResponseType,
  doCapitalize?: boolean
): {
  short: string;
  long: string;
} {
  const freq = { short: '', long: '' };
  const validDaysOfWeek = getValidDaysOfWeek(loc.valid_days_of_week);

  if (loc.repeating) {
    const days = [];
    validDaysOfWeek.reduce((days, day) => {
      days.push(DaysOfWeekMap[day]);
      return days;
    }, days);

    if (days.length > 1) {
      days[days.length - 1] = `and ${days[days.length - 1]}`;
    }

    const delimiter = days.length === 2 ? ' ' : ', '; // no comma needed if there are only two days
    freq.long = `every ${days.join(delimiter)}`;
    freq.short = freq.long;

    if (validDaysOfWeek.length === DEFAULT_WEEK_LENGTH_DAYS) {
      freq.short = 'every day';
    } else if (
      validDaysOfWeek.length === 5 &&
      [1, 2, 3, 4, 5].every((d) => validDaysOfWeek.includes(d))
    ) {
      freq.short = 'every weekday';
    }
  } else {
    // does not repeat
    const freqStr = `on ${dateFormat(
      getFirstInstanceDateUTC(loc.first_instance_utc),
      DATE_FORMAT
    )}`;

    freq.short = freqStr;
    freq.long = freqStr;
  }

  if (doCapitalize) {
    freq.short = capitalize(freq.short);
    freq.long = capitalize(freq.long);
  }

  return freq;
}

export function getFirstInstanceDateUTC(firstInstanceISOStr: string): Date {
  return utcToZonedTime(firstInstanceISOStr, 'UTC');
}

export function getFirstInstanceDateFormatted(
  firstInstanceDate: string,
  format: string
): string {
  return dateFormat(getFirstInstanceDateUTC(firstInstanceDate), format);
}

export function getFormattedLocBudgetAmount(
  loc: ILineOfCreditResponseType
): string {
  return loc.tag_invites && loc.tag_invites.length > 0
    ? `${
        loc.tag_invites.length &&
        currencyFormatter(
          Number(transformPenniesToDollar(loc.tag_invites[0].budget))
        )
      }`
    : '$0';
}

export function getMealCreditBudgetString(
  budgetAmountInPennies: number,
  numberOfDecimals = 0
): string {
  if (budgetAmountInPennies === UNLIMITED_BUDGET_PENNIES) {
    return 'Unlimited Budget';
  }

  return currencyFormatter(
    Number(transformPenniesToDollar(budgetAmountInPennies)),
    numberOfDecimals
  );
}

export function mapToFormValues(
  locResponse: ILineOfCreditResponseType
): ILocFormFields {
  const {
    name,
    valid_days_of_week,
    tag_invites,
    first_instance_utc,
    budget_reset_period,
    repeating,
  } = locResponse;

  const budgetLength = budget_reset_period === 1 ? 'day' : 'week';

  const fromDate = getFirstInstanceDateUTC(first_instance_utc);

  const date =
    budgetLength === 'week'
      ? { from: fromDate, to: addDays(fromDate, 7) }
      : fromDate;

  const amount = currencyFormatter(
    Number(transformPenniesToDollar(tag_invites[0].budget))
  );
  const locFormValues = {
    creditName: name,
    budgetLength: budget_reset_period === 1 ? 'day' : 'week',
    amount,
    date,
    frequency: repeating ? 'repeats' : 'single',
    days: valid_days_of_week,
  } as ILocFormFields;

  return locFormValues;
}

export function getFirstInstanceUtcPayloadString(
  firstInstanceUtc: Date
): string {
  return dateFormat(
    startOfDay(firstInstanceUtc),
    "yyyy-MM-dd'T'HH:mm:ss'.000Z'"
  );
}

export function mapToPayload(values: ILocFormFields): ILineOfCreditPayload {
  const {
    budgetLength,
    creditName,
    amount,
    date,
    frequency,
    days = [],
  } = values;

  let validDaysOfWeek = days.filter((day) => !!day);
  let budgetResetPeriod = 1;
  let firstInstanceUtc = date as Date;
  const budgetAmount = amount.replace(/[$,]/g, '');

  if (budgetLength === 'week') {
    validDaysOfWeek = DAYS.reduce((validDays, day, dayIndex) => {
      if (day) {
        validDays.push(dayIndex);
      }

      return validDays;
    }, []);

    budgetResetPeriod = 7;
    firstInstanceUtc = (date as RangeModifier).from;
  }

  let repeating = true;
  if (frequency === 'single') {
    // WDA-25867 Non-repeating LOCs should always have `valid_days_of_week` set to every day
    validDaysOfWeek = [1, 2, 3, 4, 5, 6, 7];
    repeating = false;
  }

  const budgetPayload = {
    name: creditName,
    valid_days_of_week: validDaysOfWeek,
    diner_budget: transformDollarToPennies(Number(budgetAmount)),
    first_instance_utc: getFirstInstanceUtcPayloadString(firstInstanceUtc),
    budget_reset_period: budgetResetPeriod,
    repeating,
    instance_type: 'DAYS',
  } as ILineOfCreditPayload;

  return budgetPayload;
}

function getValidDaysOfWeek(validDaysOfWeek: number[]): number[] {
  if (validDaysOfWeek.length > 0) {
    return validDaysOfWeek;
  }

  // if an empty array is provided, it should be treated as valid every day
  // the backend team will work to add a validation rule to make sure this can't be
  // created in the future
  return VALID_EVERY_DAY;
}

export function getWeeklyMealCreditEndDate(
  startDate: Date,
  lengthOfCreditDays: number
): Date {
  // for correct displaying, the last date is 11:59 pm of the previous day of the period
  // e.g. 7 days it's actually 6 days 23 hours 59 min
  return addMinutes(addDays(startDate, lengthOfCreditDays), -1);
}
